import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Renderer2,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { Store } from '@ngrx/store';
import { LogoutConfirmationModalComponent } from 'src/app/features/authentication/components/logout-confirmation/logout-confirmation.component';
import { environment } from 'src/environments/environment';
import { FeatureFlagService } from '../../services/feature-flags/feature-flag.service';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { FormatNamePipe } from '../../../shared/pipes/format-name.pipe';
import { ListItem } from '../../models/item-list';
import { AuthService } from '../../services/auth/auth.service';
import { AnnouncementsService } from '../../services/communication/announcements.service';
import { RouterService } from '../../services/router/router.service';
import {
  selectGuardianAvatar,
  selectIsLoggedInUserAccountHolder,
  selectLoggedInGuardianFirstStudentId,
} from '../../store/guardian/guardian.selectors';

@Component({
  selector: 'app-side-nav',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AvatarComponent, IconComponent, RouterLink, MatBadgeModule, FormatNamePipe, RouterLinkActive],
  templateUrl: './side-nav.component.html',
  styleUrl: './side-nav.component.scss',
})
export class SideNavComponent {
  private readonly store = inject(Store);
  private readonly authService = inject(AuthService);
  private readonly routerService = inject(RouterService);
  private readonly renderer = inject(Renderer2);
  private readonly announcementService = inject(AnnouncementsService);
  private readonly dialog = inject(MatDialog);
  private readonly featureFlags = inject(FeatureFlagService);

  currentUrl = computed(() => this.routerService.currentUrl() ?? '/home');

  showNav = computed(() => {
    const isAuthenticated = this.authService.isAuthenticated();
    return isAuthenticated === true || environment.production === false;
  });

  private readonly showFinance = toSignal(this.store.select(selectIsLoggedInUserAccountHolder), {
    initialValue: false,
  });

  unreadCount = this.announcementService.serverUnreadCount;

  menu = viewChild<ElementRef>('menu');

  user = toSignal(this.store.select(selectGuardianAvatar(48, '#0B2A5B')));
  userName = computed(() => this.user()?.name ?? '');
  firstChildId = toSignal(this.store.select(selectLoggedInGuardianFirstStudentId));

  menuOpen = signal(false);
  currentApplicationVersion = environment.appVersion;

  isProfileActive = computed(() => {
    const user = this.user();
    if (!user) return false;
    return this.currentUrl() === `/guardians/${user.id}/profile`;
  });

  isToggleMenuActive = computed(() => {
    const url = this.currentUrl();
    return (
      (!this.menuOpen() &&
        this.submenuItems().some((item) => url.startsWith(item.link) && !url.endsWith('/profile'))) ||
      url === '/enrolments'
    );
  });

  constructor() {
    this.renderer.listen('window', 'click', (e: Event) => {
      if (!this.menu()?.nativeElement.contains(e.target)) {
        this.menuOpen.set(false);
      }
    });
  }

  menuItems = computed<ListItem[]>(() => {
    const firstChildId: string = this.firstChildId() ?? '';
    const studentLink = `/learners/${firstChildId}/profile`;
    const url = this.currentUrl();

    const isSchoolLifeActive = url?.includes('/learners/') ?? false;
    const isCalendarActive = url === '/events';
    const isCommunicationActive = url?.includes('/inbox/') ?? false;
    const isHomeActive = url === '/home';

    return [
      {
        icon: 'my-hub',
        label: 'Home',
        link: '/home',
        isActive: isHomeActive,
      },
      {
        icon: isSchoolLifeActive ? 'school-life-active' : 'school-life-inactive',
        label: 'School life',
        link: studentLink,
        isActive: isSchoolLifeActive,
      },
      {
        icon: isCalendarActive ? 'calendar-active' : 'calendar-inactive',
        label: 'Calendar',
        link: '/events',
        isActive: isCalendarActive,
      },
      {
        icon: isCommunicationActive ? 'mail' : 'email-inactive',
        label: 'Communication',
        link: '/inbox/announcements',
        isActive: isCommunicationActive,
      },
    ];
  });

  submenuItems = computed<ListItem[]>(() => {
    const url = this.currentUrl();
    const addActive = (item: { label: string; link: string }): ListItem => ({
      ...item,
      isActive: (item.link === '/guardians' && url === '/enrolments') || url === item.link,
    });

    const baseItems = [
      { label: 'Manage my family', link: '/guardians' },
      { label: 'Help and support', link: '/help' },
      ...(this.featureFlags.get('library') ? [{ label: 'Library', link: '/library' }] : []),
    ];

    const items = this.showFinance() ? [{ label: 'Finance', link: '/payments' }, ...baseItems] : baseItems;

    return items.map(addActive);
  });

  toggleMenu() {
    this.menuOpen.set(!this.menuOpen());
  }

  logout() {
    this.dialog.open(LogoutConfirmationModalComponent, {
      height: '440px',
      width: '488px',
    });
  }

  navigateToProfile(event: Event) {
    this.menuOpen.set(false);
    event.stopPropagation();
  }
}
